import {useRef} from 'react';
import {shallowEqual} from 'react-redux';
import {createSelector} from 'reselect';

import {useWorkspaceRefsContext} from '../../components/Workspace/WorkspaceRefsContext';
import {useAutoOrganizePrefix} from '../../components/WorkspaceDrawer/Settings/hooks';
import {type OrganizationPrefix} from '../../components/WorkspaceDrawer/Settings/types';
import {type RootState} from '../../types/redux';
import {
  PANEL_BANK_CHARTS_NAME,
  PANEL_BANK_CUSTOM_CHARTS_NAME,
  PANEL_BANK_CUSTOM_VISUALIZATIONS_NAME,
  PANEL_BANK_EVALUATION_NAME,
  PANEL_BANK_EVALUATION_TABLES_LEGACY_NAME,
  PANEL_BANK_EVALUATION_TABLES_NAME,
  PANEL_BANK_HIDDEN_SECTION_NAME,
  PANEL_BANK_MEDIA_NAME,
  PANEL_BANK_SYSTEM_NAME,
  PANEL_BANK_TABLES_NAME,
} from '../../util/panelbankTypes';
import {getMetrics} from '../../util/panels';
import {type LayedOutPanel} from '../../util/panelTypes';
import {getPrefixSectionName} from '../../util/prefixSectionName';
import {useSelector} from '../hooks';
import {makeSectionRefsSelector} from '../views/panelBankConfig/selectors';

/**
 * Pinned sections to send as `requiredPrefixes`, so the section cap
 * (`maxPrefixGroups`) never drops them.
 *
 * Changing `requiredPrefixes` sends a new request, so pinning and unpinning
 * do not change it directly:
 *
 * - New pins are added when another query variable changes (`baseVariables`),
 *   since a request is going out then anyway. A section you can pin is
 *   already on screen, so it only needs protecting from that next request,
 *   for example the one that clears a panel search.
 * - Unpinned sections are never removed. A section that was only on screen
 *   because it was pinned stays until the workspace reloads.
 */
export function useRequiredPrefixes(baseVariables: object): string[] {
  const pinnedPrefixes = usePinnedPrefixes();
  // Using a ref rather than state avoids the extra render a setState during render would cost
  const sentRef = useRef({baseVariables, prefixes: pinnedPrefixes});
  // oxlint-disable-next-line react/refs
  const sent = sentRef.current;
  // oxlint-disable-next-line react/refs
  if (sent.baseVariables !== baseVariables) {
    // oxlint-disable-next-line react/refs
    sentRef.current = {
      baseVariables,
      // oxlint-disable-next-line react/refs
      prefixes: [...new Set([...sent.prefixes, ...pinnedPrefixes])].sort(),
    };
  }
  // oxlint-disable-next-line react/refs
  return sentRef.current.prefixes;
}

function usePinnedPrefixes(): string[] {
  const {panelBankConfigRef} = useWorkspaceRefsContext();
  const autoOrganizePrefix = useAutoOrganizePrefix();
  const selector = createSelector(
    makeSectionRefsSelector(panelBankConfigRef),
    (state: RootState) => state.views.parts['panel-bank-section-config'],
    (state: RootState) => state.views.parts.panel,
    (sectionRefs, sectionParts, panelParts) =>
      getPinnedSectionPrefixes(
        sectionRefs.map(sectionRef => {
          const section = sectionParts[sectionRef.id];
          return {
            defaultName: section.defaultName,
            pinned: section.pinned,
            panels: section.panelRefs.map(panelRef => panelParts[panelRef.id]),
          };
        }),
        autoOrganizePrefix
      )
  );
  return useSelector(selector, shallowEqual);
}

/** Default sections that are not backed by a summary-metric key prefix. */
const NON_PREFIX_DEFAULT_SECTION_NAMES = new Set([
  PANEL_BANK_CHARTS_NAME,
  PANEL_BANK_MEDIA_NAME,
  PANEL_BANK_TABLES_NAME,
  PANEL_BANK_SYSTEM_NAME,
  PANEL_BANK_HIDDEN_SECTION_NAME,
  PANEL_BANK_CUSTOM_VISUALIZATIONS_NAME,
  PANEL_BANK_EVALUATION_TABLES_LEGACY_NAME,
  PANEL_BANK_EVALUATION_TABLES_NAME,
  PANEL_BANK_EVALUATION_NAME,
  PANEL_BANK_CUSTOM_CHARTS_NAME,
]);

/**
 * Derives `sectionAwareHistoryKeys.requiredPrefixes` from pinned workspace sections.
 * Only prefix-based sections are included. Non-prefixed (type-based) sections
 * (i.e. Charts/Media/Tables) are excluded because SAHK always returns those.
 *
 * Auto panels in a pinned manual section contribute their metric's prefix,
 * split the way the workspace organizes sections. SAHK groups by the same
 * setting (`lastPrefix`), and only a required prefix that equals one of its
 * group names protects anything.
 */
export function getPinnedSectionPrefixes(
  sections: Array<{
    defaultName?: string;
    pinned: boolean;
    panels?: ReadonlyArray<LayedOutPanel>;
  }>,
  autoOrganizePrefix?: OrganizationPrefix
): string[] {
  const prefixes = new Set<string>();
  for (const section of sections) {
    if (!section.pinned) {
      continue;
    }

    if (
      section.defaultName &&
      NON_PREFIX_DEFAULT_SECTION_NAMES.has(section.defaultName)
    ) {
      continue;
    }

    if (section.defaultName) {
      prefixes.add(section.defaultName);
    }

    for (const panel of section.panels ?? []) {
      if (panel.isAuto === false) {
        continue;
      }
      for (const metric of getMetrics(panel)) {
        const prefix = getPrefixSectionName(metric, autoOrganizePrefix);
        if (prefix != null) {
          prefixes.add(prefix);
        }
      }
    }
  }
  return [...prefixes].sort();
}
